import API from '~config/api';
import {
  ApiPromise,
  DataResponse,
  ErrorResponse,
  DataPaginateResponse,
  PaginationRequestParams,
} from '~globals/types';

import {
  BillingItemCompanyBase,
  BillingItemsData,
  BillingItemsFilter,
  SettlementBillingItemMetaData,
  SettlementBillingItemGenerateBody,
  SettlementBillingItemGenerateResponse,
  SettlementBillingItemsData,
  SettlementBillingItemsFilter,
  BillingItemCompanyExtended,
  RebillItemsBody,
} from './types';

export const BILLING_ITEM_ENDPOINTS = {
  getCompaniesListToBillingItem: '/api/v1/Route/BillingList/CompaniesList',
  searchBillingItemsMinimal: '/api/v1/Route/BillingItem/headerSearch',
  searchBillingItems: '/api/v1/Route/BillingItem/search',
  getSettlementBillingItemMetaData:
    '/api/v1/Route/SettlementBillingItem/GenerateMetaData',
  generateSettlementBillingItem: '/api/v1/Route/SettlementBillingItem/Generate',
  searchSettlementBillingItems: '/api/v1/Route/SettlementBillingItem/search',
  rebillItems: '/api/v1/Route/BillingItem/Rebill',
  exportSettlementPDF: '/api/v1/Report/SettlementPDF',
};

export const getCompaniesListToBillingItem = (): ApiPromise<
  DataResponse<{ result: BillingItemCompanyBase[] }>,
  ErrorResponse
> =>
  API.get<DataResponse<{ result: BillingItemCompanyBase[] }>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.getCompaniesListToBillingItem,
  );

export const searchBillingItemsMinimal = ({
  pagination: { page, sizePerPage },
  filters,
}: PaginationRequestParams<BillingItemsFilter>): ApiPromise<
  DataPaginateResponse<BillingItemCompanyExtended[]>,
  ErrorResponse
> =>
  API.post<DataPaginateResponse<BillingItemCompanyExtended[]>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.searchBillingItemsMinimal,
    {
      ...filters,
      pagination: { offset: page, size: sizePerPage },
    },
  );

export const searchBillingItems = (
  filters: BillingItemsFilter,
): ApiPromise<DataPaginateResponse<BillingItemsData[]>, ErrorResponse> =>
  API.post<DataPaginateResponse<BillingItemsData[]>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.searchBillingItems,
    {
      ...filters,
      pagination: { offset: 1, size: -1 },
    },
  );

export const searchBillingItemsToExport = ({
  pagination: { page, sizePerPage },
  filters,
}: PaginationRequestParams<BillingItemsFilter>): ApiPromise<
  Blob,
  ErrorResponse
> =>
  API.post<Blob, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.searchBillingItems,
    {
      ...filters,
      exportCSV: true,
      pagination: { offset: page, size: sizePerPage },
    },
    { responseType: 'blob', timeout: 120000 },
  );

export const getSettlementBillingItemMetaData = (): ApiPromise<
  DataResponse<SettlementBillingItemMetaData>,
  ErrorResponse
> =>
  API.get<DataResponse<SettlementBillingItemMetaData>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.getSettlementBillingItemMetaData,
  );

export const generateSettlementBillingItem = (
  body: SettlementBillingItemGenerateBody,
): ApiPromise<
  DataResponse<SettlementBillingItemGenerateResponse>,
  ErrorResponse
> =>
  API.post<DataResponse<SettlementBillingItemGenerateResponse>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.generateSettlementBillingItem,
    body,
  );

export const searchSettlementBillingItems = ({
  pagination: { page, sizePerPage },
  filters,
}: PaginationRequestParams<SettlementBillingItemsFilter>): ApiPromise<
  DataPaginateResponse<SettlementBillingItemsData[]>,
  ErrorResponse
> =>
  API.post<DataPaginateResponse<SettlementBillingItemsData[]>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.searchSettlementBillingItems,
    {
      ...filters,
      pagination: { offset: page, size: sizePerPage },
    },
  );

export const searchSettlementBillingItemsToExport = ({
  pagination: { page, sizePerPage },
  filters,
}: PaginationRequestParams<SettlementBillingItemsFilter>): ApiPromise<
  Blob,
  ErrorResponse
> =>
  API.post<Blob, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.searchSettlementBillingItems,
    {
      ...filters,
      exportCSV: true,
      pagination: { offset: page, size: sizePerPage },
    },
    { responseType: 'blob', timeout: 120000 },
  );

export const rebillItems = (
  body: RebillItemsBody,
): ApiPromise<DataResponse<null>, ErrorResponse> =>
  API.post<DataResponse<null>, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.rebillItems,
    body,
  );

export const exportSettlementPDF = (
  settlementId: string,
): ApiPromise<Blob, ErrorResponse> =>
  API.post<Blob, ErrorResponse>(
    BILLING_ITEM_ENDPOINTS.exportSettlementPDF,
    { settlementId },
    { responseType: 'blob', timeout: 120000 },
  );
